/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AIMetricsPanel - WASP Frontend v2.2.0
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file AIMetricsPanel.tsx
 * @version 1.0.0
 * @description Panel de métricas agregadas y estadísticas de IA
 * @author WASP Development Team
 * 
 * FUNCIONALIDADES:
 * - KPI Cards (4 métricas principales)
 * - Gráfico de tendencias (últimos 30 días)
 * - Gauge de success rate
 * - Tabla de endpoints más usados
 * - Tabla de endpoints con mejor performance
 * - Métricas de tokens y costos
 * 
 * NOTA: Este componente usa mock data porque el backend aún no tiene
 * endpoints de métricas implementados.
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  Table,
  Badge,
  ProgressBar,
  Alert,
  Form
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faCheckCircle,
  faClock,
  faCoins,
  faStar,
  faBolt,
  faTrophy,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAIEndpoints } from '../../../../../hooks/useAIEndpoints';
import { AIMetrics } from '../../../../../types/ai.types';

// ═══════════════════════════════════════════════════════════════════════════
// MOCK DATA (temporal hasta que backend tenga endpoints)
// ═══════════════════════════════════════════════════════════════════════════

const MOCK_METRICS: AIMetrics = {
  total_interactions: 1245,
  success_rate: 94.5,
  avg_response_time: 1850,
  total_tokens: 156780,
  avg_rating: 4.3,
  interactions_by_day: [
    { date: '2025-01-18', count: 45, success: 42, errors: 3 },
    { date: '2025-01-17', count: 52, success: 50, errors: 2 },
    { date: '2025-01-16', count: 38, success: 36, errors: 2 },
    { date: '2025-01-15', count: 41, success: 39, errors: 2 },
    { date: '2025-01-14', count: 47, success: 45, errors: 2 },
    { date: '2025-01-13', count: 33, success: 31, errors: 2 },
    { date: '2025-01-12', count: 28, success: 27, errors: 1 }
  ],
  top_endpoints: [
    { endpoint_id: 'ep_001', name: 'IRC GPT-4', count: 456, success_rate: 96.2 },
    { endpoint_id: 'ep_002', name: 'Anubis Claude', count: 389, success_rate: 92.8 },
    { endpoint_id: 'ep_003', name: 'OpenRouter', count: 234, success_rate: 94.1 },
    { endpoint_id: 'ep_004', name: 'Local LLM', count: 166, success_rate: 89.3 }
  ]
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const AIMetricsPanel: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────────
  // Hooks
  // ───────────────────────────────────────────────────────────────────────
  
  const { endpoints } = useAIEndpoints();

  // ───────────────────────────────────────────────────────────────────────
  // Estado Local
  // ───────────────────────────────────────────────────────────────────────
  
  const [metrics] = useState<AIMetrics>(MOCK_METRICS);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('7d');

  // Filtrar datos según rango de tiempo
  const filteredData = useMemo(() => {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    return metrics.interactions_by_day.slice(-days);
  }, [metrics, timeRange]);

  // ───────────────────────────────────────────────────────────────────────
  // Render - KPI Cards
  // ───────────────────────────────────────────────────────────────────────
  
  const renderKPICards = () => (
    <Row className="mb-4">
      <Col md={3}>
        <Card className="border-0 shadow-sm h-100">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted d-block mb-1">Total Interacciones</small>
                <h3 className="mb-0">{metrics.total_interactions.toLocaleString()}</h3>
              </div>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 48, height: 48, backgroundColor: '#e0e7ff' }}
              >
                <FontAwesomeIcon icon={faChartLine} className="text-primary" size="lg" />
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col md={3}>
        <Card className="border-0 shadow-sm h-100">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted d-block mb-1">Tasa de Éxito</small>
                <h3 className="mb-0">{metrics.success_rate}%</h3>
              </div>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 48, height: 48, backgroundColor: '#d1fae5' }}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="text-success" size="lg" />
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col md={3}>
        <Card className="border-0 shadow-sm h-100">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted d-block mb-1">Tiempo Promedio</small>
                <h3 className="mb-0">{metrics.avg_response_time}ms</h3>
              </div>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 48, height: 48, backgroundColor: '#fef3c7' }}
              >
                <FontAwesomeIcon icon={faClock} className="text-warning" size="lg" />
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col md={3}>
        <Card className="border-0 shadow-sm h-100">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted d-block mb-1">Tokens Totales</small>
                <h3 className="mb-0">{(metrics.total_tokens / 1000).toFixed(1)}K</h3>
              </div>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 48, height: 48, backgroundColor: '#dbeafe' }}
              >
                <FontAwesomeIcon icon={faCoins} className="text-info" size="lg" />
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  // ───────────────────────────────────────────────────────────────────────
  // Render - Gráfico de Tendencias (SVG nativo)
  // ───────────────────────────────────────────────────────────────────────
  
  const renderTrendsChart = () => {
    const maxCount = Math.max(...filteredData.map(d => d.count));
    const width = 800;
    const height = 250;
    const padding = 40;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    // Calcular puntos
    const points = filteredData.map((d, i) => {
      const x = padding + (i / (filteredData.length - 1)) * chartWidth;
      const y = padding + chartHeight - (d.count / maxCount) * chartHeight;
      return { x, y, data: d };
    });

    // Path del área
    const areaPath = [
      `M ${padding} ${padding + chartHeight}`,
      ...points.map(p => `L ${p.x} ${p.y}`),
      `L ${padding + chartWidth} ${padding + chartHeight}`,
      'Z'
    ].join(' ');

    // Path de la línea
    const linePath = points.map((p, i) => 
      `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
    ).join(' ');

    return (
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />
              Tendencia de Interacciones
            </h5>
            <Form.Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              style={{ width: 150 }}
            >
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
              <option value="90d">Últimos 90 días</option>
            </Form.Select>
          </div>

          <div style={{ width: '100%', overflowX: 'auto' }}>
            <svg width={width} height={height} style={{ display: 'block', margin: '0 auto' }}>
              {/* Grid horizontal */}
              {[0, 0.25, 0.5, 0.75, 1].map((factor, i) => {
                const y = padding + chartHeight * (1 - factor);
                return (
                  <g key={i}>
                    <line
                      x1={padding}
                      y1={y}
                      x2={width - padding}
                      y2={y}
                      stroke="#e5e7eb"
                      strokeWidth="1"
                      strokeDasharray="4,4"
                    />
                    <text
                      x={padding - 10}
                      y={y + 4}
                      textAnchor="end"
                      fontSize="12"
                      fill="#6b7280"
                    >
                      {Math.round(maxCount * factor)}
                    </text>
                  </g>
                );
              })}

              {/* Área bajo la curva */}
              <path
                d={areaPath}
                fill="url(#gradient)"
                opacity="0.3"
              />

              {/* Línea principal */}
              <path
                d={linePath}
                fill="none"
                stroke="#8b5cf6"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Puntos */}
              {points.map((point, i) => (
                <g key={i}>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="5"
                    fill="#8b5cf6"
                    stroke="#fff"
                    strokeWidth="2"
                  />
                  {/* Etiqueta de fecha */}
                  {i % Math.ceil(filteredData.length / 7) === 0 && (
                    <text
                      x={point.x}
                      y={height - 10}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#6b7280"
                    >
                      {new Date(point.data.date).toLocaleDateString('es', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </text>
                  )}
                </g>
              ))}

              {/* Gradiente */}
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <div className="text-center mt-2">
            <small className="text-muted">
              Promedio: <strong>{Math.round(filteredData.reduce((sum, d) => sum + d.count, 0) / filteredData.length)}</strong> interacciones/día
            </small>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Success Rate Gauge (SVG nativo)
  // ───────────────────────────────────────────────────────────────────────
  
  const renderSuccessGauge = () => {
    const percentage = metrics.success_rate;
    const radius = 80;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    // Color según porcentaje
    const getColor = () => {
      if (percentage >= 95) return '#10b981';
      if (percentage >= 85) return '#3b82f6';
      if (percentage >= 70) return '#f59e0b';
      return '#ef4444';
    };

    return (
      <Card className="border-0 shadow-sm h-100">
        <Card.Body className="text-center">
          <h6 className="mb-3">
            <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
            Tasa de Éxito General
          </h6>

          <svg width="200" height="200" style={{ margin: '0 auto', display: 'block' }}>
            {/* Círculo de fondo */}
            <circle
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="20"
            />
            {/* Círculo de progreso */}
            <circle
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke={getColor()}
              strokeWidth="20"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 100 100)"
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
            {/* Texto central */}
            <text
              x="100"
              y="95"
              textAnchor="middle"
              fontSize="32"
              fontWeight="bold"
              fill={getColor()}
            >
              {percentage}%
            </text>
            <text
              x="100"
              y="115"
              textAnchor="middle"
              fontSize="14"
              fill="#6b7280"
            >
              Success Rate
            </text>
          </svg>

          <div className="mt-3">
            <small className="text-muted">
              {metrics.total_interactions * (percentage / 100)} / {metrics.total_interactions} exitosas
            </small>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Top Endpoints
  // ───────────────────────────────────────────────────────────────────────
  
  const renderTopEndpoints = () => (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body>
        <h6 className="mb-3">
          <FontAwesomeIcon icon={faTrophy} className="me-2 text-warning" />
          Endpoints Más Usados
        </h6>

        <Table hover className="mb-0">
          <thead className="border-bottom">
            <tr>
              <th>#</th>
              <th>Endpoint</th>
              <th>Interacciones</th>
              <th>Success Rate</th>
            </tr>
          </thead>
          <tbody>
            {metrics.top_endpoints.map((endpoint, idx) => (
              <tr key={endpoint.endpoint_id}>
                <td>
                  <Badge bg={idx === 0 ? 'warning' : idx === 1 ? 'secondary' : 'light'}>
                    {idx + 1}
                  </Badge>
                </td>
                <td>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faBolt} className="me-2 text-primary" />
                    <strong>{endpoint.name}</strong>
                  </div>
                </td>
                <td>
                  <Badge bg="info">{endpoint.count}</Badge>
                </td>
                <td>
                  <div className="d-flex flex-column" style={{ minWidth: 100 }}>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <small className="text-muted">{endpoint.success_rate}%</small>
                    </div>
                    <ProgressBar
                      now={endpoint.success_rate}
                      variant={
                        endpoint.success_rate >= 95 ? 'success' :
                        endpoint.success_rate >= 85 ? 'info' :
                        endpoint.success_rate >= 70 ? 'warning' : 'danger'
                      }
                      style={{ height: 6 }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );

  // ───────────────────────────────────────────────────────────────────────
  // Render - Rating Promedio
  // ───────────────────────────────────────────────────────────────────────
  
  const renderAverageRating = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FontAwesomeIcon
          key={i}
          icon={faStar}
          className={i <= Math.round(metrics.avg_rating) ? 'text-warning' : 'text-secondary'}
          style={{ opacity: i <= Math.round(metrics.avg_rating) ? 1 : 0.3 }}
        />
      );
    }

    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="text-center">
          <h6 className="mb-3">Rating Promedio</h6>
          <div className="mb-2" style={{ fontSize: '2rem' }}>
            {stars}
          </div>
          <h3 className="mb-1">{metrics.avg_rating.toFixed(1)}</h3>
          <small className="text-muted">de 5.0</small>
        </Card.Body>
      </Card>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render Principal
  // ───────────────────────────────────────────────────────────────────────
  
  return (
    <div>
      {/* Warning - Mock Data */}
      <Alert variant="warning" className="mb-3">
        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
        <strong>Modo Demo:</strong> Este componente muestra datos de ejemplo. 
        Los endpoints de métricas aún no están implementados en el backend.
      </Alert>

      {/* KPI Cards */}
      {renderKPICards()}

      {/* Gráfico de Tendencias */}
      {renderTrendsChart()}

      {/* Fila inferior */}
      <Row>
        <Col lg={4}>
          {renderSuccessGauge()}
        </Col>
        <Col lg={4}>
          {renderAverageRating()}
        </Col>
        <Col lg={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="mb-3">
                <FontAwesomeIcon icon={faCoins} className="me-2 text-info" />
                Tokens Consumidos
              </h6>
              <h2 className="mb-1 text-primary">
                {(metrics.total_tokens / 1000).toFixed(1)}K
              </h2>
              <small className="text-muted">tokens totales</small>
              <hr />
              <div className="text-start">
                <small className="text-muted d-block mb-1">Promedio por interacción:</small>
                <strong>{Math.round(metrics.total_tokens / metrics.total_interactions)}</strong> tokens
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          {renderTopEndpoints()}
        </Col>
      </Row>
    </div>
  );
};

export default AIMetricsPanel;

// ═══════════════════════════════════════════════════════════════════════════
// FIN DEL ARCHIVO
// ═══════════════════════════════════════════════════════════════════════════